// Temporary verification errors must not destroy an otherwise valid login.
export const isTerminalSessionError = error => [
  'session_not_found', 'refresh_token_not_found', 'refresh_token_already_used',
  'user_not_found', 'user_banned', 'bad_jwt', 'invalid_jwt', 'PGRST301', 'PGRST303',
].includes(error?.code)

export function clearCachedAdminIdentity() {
  for (const storage of [localStorage, sessionStorage]) {
    for (const key of ['admin_authenticated', 'admin_user', 'admin_session_id',
      'admin_mfa_verified_session_id', 'admin_mfa_challenge_completed',
      'admin_mfa_challenge_required', 'admin_mfa_factor_id']) storage.removeItem(key)
  }
}

export function safeAdminReturnPath(value) {
  if (typeof value !== 'string' || !/^\/admin(?:[/?#]|$)/.test(value) || value.includes('\\') || [...value].some(char => char.charCodeAt(0) < 32)) return '/admin'
  const parsed = new URL(value, 'https://dashboard.invalid')
  if (parsed.origin !== 'https://dashboard.invalid' || !/^\/admin(?:\/|$)/.test(parsed.pathname)) return '/admin'
  if (['/admin/login', '/admin/mfa-setup'].includes(parsed.pathname)) return '/admin'
  for (const key of ['access_token', 'refresh_token', 'code', 'token_hash']) parsed.searchParams.delete(key)
  const hash = /(?:access_token|refresh_token|token_hash)=/.test(parsed.hash) ? '' : parsed.hash
  return `${parsed.pathname}${parsed.search}${hash}`
}

export function getAdminReturnPath(location) {
  return safeAdminReturnPath(location?.state?.from || localStorage.getItem('admin_login_next_path') || '/admin')
}

export function rememberAdminReturnPath() {
  const { pathname, search, hash } = window.location
  if (pathname !== '/admin/login' && pathname !== '/admin/mfa-setup') {
    localStorage.setItem('admin_login_next_path', safeAdminReturnPath(`${pathname}${search}${hash}`))
  }
}

export async function checkAdminAccess(client, requiredRole) {
  try {
    const { data, error } = await client.auth.getSession()
    if (error) return { status: isTerminalSessionError(error) ? 'login' : 'retry' }
    const session = data?.session
    if (!session) return { status: 'login' }
    const { data: user, error: profileError } = await client.from('admin_users')
      .select('id, username, role, is_active, mfa_enabled, auth_user_id')
      .eq('auth_user_id', session.user.id).maybeSingle()
    if (profileError) return { status: isTerminalSessionError(profileError) ? 'login' : 'retry' }
    if (!user || !user.is_active) return { status: 'denied', message: !user ? 'Your account does not have dashboard access.' : 'Your account is inactive. Please contact your administrator.' }
    const allowed = !requiredRole || user.role === 'super_admin' || (requiredRole === 'coordinator' && user.role === 'coordinator')
    if (!allowed) return { status: 'denied', message: 'You do not have access to this page.' }
    const { data: factors, error: factorError } = await client.auth.mfa.listFactors()
    if (factorError) return { status: isTerminalSessionError(factorError) ? 'login' : 'retry' }
    const verified = [...(factors?.totp || []), ...(factors?.all || [])]
      .filter(factor => factor.status === 'verified' || factor.verified === true)
    if (!verified.length) return { status: 'setup' }
    const { data: assurance, error: assuranceError } = await client.auth.mfa.getAuthenticatorAssuranceLevel()
    if (assuranceError || !assurance?.currentLevel) return { status: 'retry' }
    if (assurance.currentLevel !== 'aal2') return { status: 'challenge', factorId: verified[0].id }
    return { status: 'ready', user, authUserId: session.user.id }
  } catch (_) { return { status: 'retry' } }
}

export async function withSessionCheckTimeout(promise, timeout = 12000) {
  let timer
  try {
    return await Promise.race([promise, new Promise(resolve => { timer = setTimeout(() => resolve({ status: 'retry' }), timeout) })])
  } finally { clearTimeout(timer) }
}
